
import { isAppUsing, isDebugMode } from '@@/bits/flip'
import { PiniaMapAction, PiniaMapState } from '@@/bits/pinia'
import { useCommandsStore } from '@@/pinia/commands'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import device from 'bits/device'
import { Component, Getter, State, Vue, defineAsyncComponent, namespace } from 'bits/vue'
import { initializeZoomSdk } from 'bits/zoom'
import { useWindowSizeStore } from 'pinia/window_size'
import tinykeys from 'tinykeys'
import SurfaceOffline from 'vuecomponents/surface_offline.vue'
import DarkModeMixin from 'vuemixins/dark_mode_mixin'
import FullscreenMixin from 'vuemixins/fullscreen_mixin'
import GlobalStylesMixin from 'vuemixins/global_styles_mixin'
import NativeAppMixin from 'vuemixins/native_app_mixin'
import PageStylingMixin from 'vuemixins/page_styling_mixin'
import PresenceMixin from 'vuemixins/presence_mixin'
import VisualViewportSizeMixin from 'vuemixins/visual_viewport_size_mixin'
import WindowSizeMixin from 'vuemixins/window_size_mixin'

const mixins: any[] = [
  GlobalStylesMixin,
  PageStylingMixin,
  FullscreenMixin,
  WindowSizeMixin,
  PresenceMixin,
  DarkModeMixin,
  VisualViewportSizeMixin,
]
if (device.app) {
  mixins.push(NativeAppMixin)
}

@Component({
  components: {
    // Don't lazy load SurfaceOffline. Otherwise, if the internet disconnects, we need to show this component,
    // but we can't show it because we can't fetch it.
    SurfaceOffline,
    SurfaceContainer: defineAsyncComponent(() => import('vuecomponents/surface_container.vue')),
    AppGlobalSnackbar: defineAsyncComponent(() => import('vuecomponents/app_global_snackbar.vue')),
    GlobalSnackbarVuex: defineAsyncComponent(() => import('vuecomponents/GlobalSnackbarVuex.vue')),
    GlobalSnackbar: defineAsyncComponent(() => import('vuecomponents/GlobalSnackbar.vue')),
    GlobalMentionAutocomplete: defineAsyncComponent(() => import('@@/vuecomponents/GlobalMentionAutocomplete.vue')),
    SurfaceDragAndDropOverlay: defineAsyncComponent(() => import('vuecomponents/surface-drag-and-drop-overlay.vue')),
    SurfaceSlideshow: defineAsyncComponent(() => import('vuecomponents/SurfaceSlideshow.vue')),
    CommandDialog: defineAsyncComponent(() => import('vuecomponents/CommandDialog.vue')),
    SurfaceExportsDocumentStatus: defineAsyncComponent(() => import('vuecomponents/SurfaceExportsDocumentStatus.vue')),
    SurfaceOnboardingPanel: defineAsyncComponent(() => import('vuecomponents/SurfaceOnboardingPanel.vue')),
    SurfaceOnboardingGalleryPanelMobile: defineAsyncComponent(
      () => import('vuecomponents/SurfaceOnboardingGalleryPanelMobile.vue'),
    ),
  },
  mixins,
})
export default class SurfaceApp extends Vue {
  isAppUsing = isAppUsing

  @State isOnline!: boolean
  @Getter isPostable!: boolean
  @Getter isSlideshow!: string
  @Getter isGeneratingPDF!: boolean
  @Getter wallId!: string

  @namespace('snackbars').Getter xSnackbar!: boolean

  @PiniaMapState(useCommandsStore) xCommandDialog!: string
  @PiniaMapAction(useCommandsStore) showCommandDialog!: () => void
  @PiniaMapState(useSurfaceOnboardingPanelStore) showOnboardingPanel!: boolean
  @PiniaMapState(useSurfaceOnboardingPanelStore) isGalleryTemplatePanel!: boolean
  @PiniaMapState(useWindowSizeStore) isBiggerThanTabletPortrait!: boolean

  removeKeyboardShortcuts = () => {}

  get isBlankOnboardingPanel() {
    return isAppUsing('onboardingPanel') && this.showOnboardingPanel && !this.isGalleryTemplatePanel
  }

  get isGalleryPanelMobile() {
    return (
      isAppUsing('onboardingPanel') &&
      this.showOnboardingPanel &&
      this.isGalleryTemplatePanel &&
      !this.isBiggerThanTabletPortrait
    )
  }

  get isGalleryPanelDesktop() {
    return (
      isAppUsing('onboardingPanel') &&
      this.showOnboardingPanel &&
      this.isGalleryTemplatePanel &&
      this.isBiggerThanTabletPortrait
    )
  }

  async configureZoomApp() {
    const zoomSdk = (await import('@zoom/appssdk')).default
    await initializeZoomSdk()
    return zoomSdk
  }

  async mounted() {
    if (device.zoomApp) {
      const zoomSdk = await this.configureZoomApp()
      zoomSdk.expandApp({ action: 'expand' })
    }
    if (isDebugMode || isAppUsing('showCommandPalette')) {
      this.removeKeyboardShortcuts = tinykeys(document.documentElement as HTMLElement, {
        '$mod+KeyK': this.showCommandDialog,
      })
    }
  }

  beforeDestroy(): void {
    if (isDebugMode || isAppUsing('showCommandPalette')) {
      this.removeKeyboardShortcuts()
    }
  }
}
